import { useDraggable } from '@dnd-kit/react';
import { IconButton } from '@/shared/ui';
import { GripVerticalIcon, SettingsIcon, Trash2Icon } from 'lucide-react';
import { FC, memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, type QualityZoneRanges } from '@/shared/lib';
import { isResolvedChart, toChartDefinition, type PresetChartFormValues } from '../../model/dashboardPresetForm';
import { chartInitialBindingValues } from '../../model/useCustomChartBindings';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { ChartLabelWidthProvider } from '../DashboardPresetGrid/ChartLabelWidthContext';
import {
  CHART_CARD_CLASS,
  CHART_CELL_CLASS,
  chartCellStyle,
  isChartShrinkingToContent,
} from '../DashboardPresetGrid/chartLayout';
import { CustomChartCard, type CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import { ChartResizeHandle } from './ChartResizeHandle';
import { NewChartPlaceholder } from './NewChartPlaceholder';
import { useChartCardResize } from './useChartCardResize';

type Props = {
  chart: PresetChartFormValues;
  /** Фильтры панели страницы: в режиме редактирования диаграммы наполнены теми же данными */
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: диаграмма окрашена ими и в режиме правки */
  zoneRanges: QualityZoneRanges;
  /** Свойства диаграммы развёрнуты блоком под её строкой */
  isPropertiesExpanded: boolean;
  /** Место сетки дашборда на странице: по нему считается доля ширины при растягивании */
  getGridRect: () => DOMRect | null;
  onToggleProperties: (chartId: string) => void;
  onRemove: (chartId: string) => void;
  onResize: (chartId: string, change: ChartSizeChange) => void;
  /** Граница колонки названий столбчатой перетащена — новая доля ширины диаграммы под названия */
  onChangeLabelWidth: (chartId: string, labelWidth: number) => void;
  /** Линии сетки на время растягивания по ширине; `null` — убрать */
  onShowGridLines: (lines: number[] | null) => void;
};

/**
 * Диаграмма правящегося дашборда: показана своими данными, но взята в обвязку правки —
 * перетаскивание за ручку, растягивание границ, свойства и удаление.
 *
 * Ячейкой своего места на сетке диаграмма встаёт сама — с той же высотой, что и на применённом
 * дашборде, а долю ширины и место в строке ей задаёт сетка: правка показывает дашборд таким, каким он
 * получится, поэтому и в строке диаграмма стоит наравне с соседками. Свойства раскрываются отдельным блоком под всей строкой.
 * У столбчатой по горизонтали перетаскивается и граница колонки названий.
 *
 * Данные берутся тем же запросом, что и у применённого дашборда, а размер в его ключ не входит:
 * растянутая диаграмма перестраивает уже полученное содержимое, не обращаясь к бэкенду заново.
 *
 * Заводимая диаграмма правила ещё не имеет: на её месте показана заготовка, а растягивания и
 * свойств у неё нет — задавать их нечему, пока описание не истолковано.
 *
 * Перерисовывается диаграмма только своей правкой: перетаскивание и растягивание соседки меняют
 * состав дашборда, и перерисовка всех диаграмм на каждую такую правку не давала бы жесту идти плавно.
 */
export const EditableChartCard: FC<Props> = memo(
  ({
    chart,
    filters,
    zoneRanges,
    isPropertiesExpanded,
    getGridRect,
    onToggleProperties,
    onRemove,
    onResize,
    onChangeLabelWidth,
    onShowGridLines,
  }) => {
    const { t } = useTranslation('qualityDashboard');
    const { chartId } = chart;
    // Ячейка диаграммы — источник перетаскивания: по её месту на странице сетка отсчитывает место
    // сброса, а растягивание показывает на ней высоту по ходу жеста
    const { ref, handleRef, isDragging } = useDraggable({ id: chartId });
    const cellRef = useRef<HTMLDivElement | null>(null);
    const setCellRef = useCallback(
      (element: HTMLDivElement | null) => {
        ref(element);
        cellRef.current = element;
      },
      [ref],
    );
    const cardRef = useRef<HTMLDivElement>(null);
    const handleResize = useCallback((change: ChartSizeChange) => onResize(chartId, change), [onResize, chartId]);
    const handleChangeLabelWidth = useCallback(
      (labelWidth: number) => onChangeLabelWidth(chartId, labelWidth),
      [onChangeLabelWidth, chartId],
    );
    const { isResizing, handlePointerDown } = useChartCardResize({
      cellRef,
      cardRef,
      size: chart.size,
      height: chart.height,
      shrinksToContent: isChartShrinkingToContent(chart.chartType),
      getGridRect,
      onResize: handleResize,
      onShowGridLines,
    });
    const isResolved = isResolvedChart(chart);
    // Определение и разрез собираются заново только с правкой самой диаграммы
    const definition = useMemo(() => (isResolvedChart(chart) ? toChartDefinition(chart) : null), [chart]);
    const bindings = useMemo(() => ({ values: chartInitialBindingValues(chart) }), [chart]);

    return (
      <div
        ref={setCellRef}
        className={cn(CHART_CELL_CLASS, isDragging && 'opacity-50')}
        style={chartCellStyle(chart.height)}
      >
        {/*
          Карточка стоит внутри отступов ячейки, а обвязка правки — по её границам: ручки и кнопки
          отсчитываются от самой карточки, а не от занятой диаграммой доли сетки вместе с зазором.
        */}
        <div className={cn('group/chart relative rounded-xl', CHART_CARD_CLASS, isResizing && 'ring-2 ring-primary')}>
          {/*
            Содержимое диаграммы живое и здесь: не поместившееся прокручивается внутри неё так же,
            как на применённом дашборде. Переходов к диалогам в режиме редактирования нет —
            диаграмма получает их незаданными и потому не показывает содержимое нажимаемым.
          */}
          <div ref={cardRef} className={CHART_CARD_CLASS}>
            {definition ? (
              <ChartLabelWidthProvider onChange={handleChangeLabelWidth}>
                <CustomChartCard
                  chart={definition}
                  filters={filters}
                  zoneRanges={zoneRanges}
                  // Дашборда вокруг правящейся диаграммы ещё нет: разрез показан своим начальным
                  // значением, а место под выбранное остаётся пустым.
                  bindings={bindings}
                />
              </ChartLabelWidthProvider>
            ) : (
              <NewChartPlaceholder />
            )}
          </div>

          <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md border bg-card/90 p-1 opacity-0 transition-opacity group-hover/chart:opacity-100 group-focus-within/chart:opacity-100">
            <div
              ref={handleRef}
              role="button"
              tabIndex={-1}
              aria-label={t('presets.editor.dragChart')}
              className="cursor-grab px-1 text-muted-foreground"
            >
              <GripVerticalIcon className="size-4" />
            </div>
            {isResolved && (
              <IconButton
                variant="ghost"
                size="icon"
                label={t('presets.editor.chartProperties')}
                aria-pressed={isPropertiesExpanded}
                icon={<SettingsIcon className={cn('size-4', isPropertiesExpanded && 'text-primary')} />}
                onClick={() => onToggleProperties(chartId)}
              />
            )}
            <IconButton
              variant="ghost"
              size="icon"
              color="destructive"
              label={t('presets.editor.removeChart')}
              icon={<Trash2Icon className="size-4" />}
              onClick={() => onRemove(chartId)}
            />
          </div>

          {isResolved && (
            <>
              <ChartResizeHandle edge="width" onPointerDown={handlePointerDown} />
              <ChartResizeHandle edge="height" onPointerDown={handlePointerDown} />
            </>
          )}
        </div>
      </div>
    );
  },
);
